import { useState } from 'react'
import {
  PurchaseFiltersContent,
  PurchaseFiltersValues,
} from '@/features/purchase-history/purchase-filters/purchase-filters-content'
import { Trans } from '@lingui/react/macro'
import { formatISO } from 'date-fns'
import { usePurchaseHistoryInfiniteQuery } from '@/features/purchase-history/use-purchase-history-infinite-query/use-purchase-history-infinite-query'
import { HistoryFilters } from '@/features/history/history-filters/history-filters'
import { createFileRoute } from '@tanstack/react-router'
import { PurchaseHistoryContent } from '@/features/purchase-history/purchase-history-content'
import { assertCanPlayBeforeLoad } from '@/features/ctrls-and-caps/assert-can-play-before-load'
import { TertiaryPageLayout } from '@/routes/-tertiary-page-layout'
import { useLingui } from '@lingui/react/macro'

export const Route = createFileRoute('/_app/purchase-history')({
  component: PurchaseHistory,
  beforeLoad: async ({ context }) => {
    return assertCanPlayBeforeLoad(context.queryClient)
  },
})

function PurchaseHistory() {
  const [filters, setFilters] = useState<PurchaseFiltersValues | null>(null)
  const { to, from } = filters || {}
  const { t } = useLingui()
  const {
    data,
    isLoading,
    isError,
    isFetchingNextPage,
    fetchNextPage,
    refetch,
  } = usePurchaseHistoryInfiniteQuery({
    query:
      from && to
        ? {
            to: formatISO(to),
            from: formatISO(from),
          }
        : undefined,
  })

  const isHeaderContentVisible = filters
    ? true
    : data?.pages?.[0]?.count > 0 && !isLoading

  return (
    <TertiaryPageLayout headerText={t`Purchase History`}>
      <header className="flex flex-col bg-surface-tertiary">
        {!!isHeaderContentVisible && (
          <div
            className="my-2 flex items-center justify-between px-4 font-family-display leading-6 font-medium text-content"
            data-testid="purchase-history-header-content"
          >
            <Trans>All Purchases</Trans>
            <HistoryFilters
              filtersCount={filters ? 1 : 0}
              filters={filters}
              title={<Trans>Purchase Filters</Trans>}
              description={
                <Trans>
                  Filter your purchase history by selecting a specific date
                  range to narrow down the results.
                </Trans>
              }
              content={
                <PurchaseFiltersContent
                  filters={filters}
                  onShowAllResults={setFilters}
                />
              }
            />
          </div>
        )}
      </header>
      <PurchaseHistoryContent
        data={data}
        isError={isError}
        isLoading={isLoading}
        filters={filters}
        refetch={refetch}
        hasNextPage={false}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
      />
    </TertiaryPageLayout>
  )
}
